import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { Table, TableColumn, Pagination } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import CheckboxHeader from '@/pages/intelligence/pages/GlobalView/components/TopMarketGenreCore/components/Table/CheckboxHeader';
import CheckboxContent from '@/pages/intelligence/pages/GlobalView/components/TopMarketGenreCore/components/Table/CheckboxContent';
import InsideTable from './components/InsideTable';
import FirstColHeader from './components/FirstColHeader';
import FirstColContent from './components/FirstColContent';
import MetricColHeader from './components/MetricColHeader';
import MetricColContent from './components/MetricColContent';
import SpecialColHeader from './components/SpecialColHeader';
import SpecialColContent from './components/SpecialColContent';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import TableImgs from '@/pages/intelligence/components/TableComponents/components/TableImgs';
import { getDetailGameOverviewPath } from '@/pages/intelligence/utils/topchartsUtils';
import { IMetricList, ITableSpecialColProp, ITableData } from '../../typings';
import { TOP_GAMES_DISPLAY_LIMIT } from '../constants';
import './index.scss';
import { useIntl, history } from 'umi';

const { pageable, scrollable, expandable } = Table.addons;

const UNIQUE_KEY = 'name';
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface IDynamicTableProps {
  className?: string;
  tableData: ITableData[];
  keyMap?: any;
  isLoading?: boolean;
  entityType?: string;
  tableSelected?: any[];
  tableSelectedAll?: any[];
  pageSize?: number;
  pageIndex?: number;
  uniqueKey?: string;
  sortKey?: string;
  sortOrder?: string;
  total?: number;
  scrollMinWidth?: number;
  pageSizeOptions?: number[];
  isNotPageableAuto?: boolean;
  isExpandable?: boolean;
  expandedTableCol?: any[];
  ratioHideCol?: string[];
  metricCol?: ITableSpecialColProp[];
  firstCol?: ITableSpecialColProp[];
  infoCol?: ITableSpecialColProp[];
  specialCol?: ITableSpecialColProp[];
  onPagingChange?: (pageIndex: number, pageSize: number) => void;
  onSortChange?: (sortKey: string, sortOrder: string) => void;
  getTextBubble?: (context: any) => React.ReactNode;
  getDateColFormat?: (context: any) => string;
  getIsHideCol?: (context: any, item: any, type?: string) => boolean;
  onGameItemClick?: (gameId: string) => void;
  onTableCheckboxChange?: (list: any, status: string) => void;
  getGenreDetailPath?: (genre: string, context: any) => string;
  isDetail?: boolean;
  nodataMsg?: string;
  dealMap?: any;
  tableIconMap?: any;
}

export default function DynamicTable(props: IDynamicTableProps) {
  const {
    className,
    tableData,
    keyMap = {},
    isLoading = false,
    entityType,
    tableSelected,
    tableSelectedAll,
    pageSize,
    pageIndex,
    uniqueKey = UNIQUE_KEY,
    sortKey,
    sortOrder,
    total,
    scrollMinWidth = 1100,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    isNotPageableAuto,
    isExpandable,
    expandedTableCol,
    ratioHideCol = [],
    metricCol = [],
    firstCol = {},
    infoCol = [],
    specialCol = [],
    onPagingChange,
    onSortChange,
    getDateColFormat = () => {},
    getIsHideCol,
    onGameItemClick,
    onTableCheckboxChange,
    getGenreDetailPath,
    isDetail = false,
    nodataMsg,
    dealMap,
    tableIconMap,
  } = props;
  const { formatMessage } = useIntl(); // 中英切换方法
  const [expandedKeys, setExpandedKeys] = useState([]);

  const goGameDetail = (gameId: string) => {
    if (gameId) history.push(getDetailGameOverviewPath(gameId, entityType));
  };

  // 分页组件是否展示
  const isSingePage = useMemo(() => {
    let min = pageSizeOptions[0];
    pageSizeOptions.forEach((num: number) => {
      if (num < min) {
        min = num;
      }
    });
    return total > min;
  }, [total, pageSizeOptions]);

  const getFirstColumns = (_config) => {
    const firstColumns = _config?.map((col) => {
      const { key, width, align } = col;
      return {
        key,
        width,
        align,
        header: (cvm: any) => (
          <FirstColHeader
            context={cvm}
            config={col}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />
        ),
        fixed: 'left',
        render: (cvm: any) => {
          const linkUrl = cvm?.linkUrl ?? getGenreDetailPath?.(keyMap[key], cvm);
          let publisher = cvm?.publisher;
          if (col?.type === 'company') {
            publisher = `${cvm?.location_city},${cvm?.location_country}`;
          }
          return (
            <FirstColContent
              context={{ ...(cvm || {}), linkUrl, publisher }}
              config={col}
              entityType={entityType}
              pageIndex={pageIndex}
              pageSize={pageSize}
              keyMap={keyMap}
              getDateColFormat={getDateColFormat}
              onGameItemClick={onGameItemClick}
              dealMap={dealMap}
            />
          );
        },
      };
    });
    if (firstCol[0]?.hasCheckbox) {
      firstColumns.unshift({
        key: 'select',
        width: 36,
        fixed: 'left',
        header: (
          <CheckboxHeader
            selected={tableSelected}
            selectedAll={tableSelectedAll}
            uniqueKey={uniqueKey}
            disabled={isLoading || tableData.length === 0}
            onChange={(list: any, status: string) => {
              onTableCheckboxChange(list, status);
            }}
          />
        ),
        render: (cvm: any) => (
          <CheckboxContent
            selected={tableSelected}
            context={cvm}
            color={cvm?.id ? ECHARTSCOLOR[cvm?.id - 1 || 0] : null}
            uniqueKey={uniqueKey}
            onChange={(list: any, status: string) => {
              onTableCheckboxChange(list, status);
            }}
          />
        ),
      });
    }
    return firstColumns;
  };

  const getMetricColumns = (_config): TableColumn[] => {
    const targetColumns = _config.filter((metricCofigItem: any) => {
      const { hideCond } = metricCofigItem;
      return !(
        hideCond &&
        getIsHideCol instanceof Function &&
        getIsHideCol(hideCond, _config, 'metric')
      );
    });
    return targetColumns?.map((metricCofigItem: any): TableColumn => {
      const { metric: metricName, source, width, align = 'right' } = metricCofigItem;
      return {
        key: `${source}_${metricName}`,
        align,
        width,
        header: () => (
          <MetricColHeader
            config={metricCofigItem}
            sortKey={sortKey}
            sortOrder={sortOrder}
            entityType={entityType}
            infoConfig={infoCol}
            onSortChange={onSortChange}
          />
        ),
        render: (cvm: any) => {
          const metricList = cvm.metrics || [];
          return (
            <MetricColContent
              config={metricCofigItem}
              metricName={metricName}
              keyMap={keyMap}
              context={metricList}
              ratioHideCol={ratioHideCol}
            />
          );
        },
      };
    });
  };

  const getSpecialColumns = () =>
    specialCol?.map((item: ITableSpecialColProp) => {
      const { key, align = 'right', width, hideCond } = item || {};
      // 特殊列会根据条件进行隐藏，具体如何隐藏由上游传递执行函数决定
      if (
        hideCond &&
        getIsHideCol instanceof Function &&
        getIsHideCol(hideCond, item, 'specical')
      ) {
        return {};
      }
      // 特殊列会根据条件进行隐藏，具体如何隐藏由上游传递执行函数决定
      return {
        key,
        align,
        width,
        header: <SpecialColHeader config={item} getIsHideCol={getIsHideCol} />,
        render: (cvm: any) => (
          <SpecialColContent
            context={cvm}
            config={item}
            hideCond={hideCond}
            getIsHideCol={getIsHideCol}
            onGameItemClick={onGameItemClick}
          />
        ),
      };
    });

  // Top Games By Revenue/Top Games By Digital Revenue
  const getTopGamesRevenueColumns = () => {
    if (isDetail) return [];
    if (entityType === 'total') return [];
    const headerName =
      entityType === 'mobile'
        ? 'dbi_genre_profile_top_game_revenue'
        : 'dbi_genre_profile_top_game_digital_revenue';
    const headerWidth = entityType === 'pc' ? 360 : entityType === 'mobile' ? 230 : 260;
    return [
      {
        key: 'top',
        align: 'center',
        width: headerWidth,
        header: formatMessage({ id: headerName }),
        render: (cvm: any) => {
          const topGameIds = Array.isArray(cvm?.game_ids)
            ? cvm.game_ids.slice(0, TOP_GAMES_DISPLAY_LIMIT)
            : [];
          return (
            <div className="dbi-genre-profile-top-revenue">
              <TableImgs
                cvm={topGameIds}
                getClick={(gameId: any) => goGameDetail(gameId.id)}
                entityType={entityType}
                tableIconMap={tableIconMap}
              />
            </div>
          );
        },
      },
    ];
  };

  const getAllColumns = (): any[] => {
    // 去掉空白列
    const specialColumns = getSpecialColumns()?.filter((col: any) => col.key) || [];
    return [
      ...getFirstColumns(firstCol),
      ...getMetricColumns(metricCol),
      ...getTopGamesRevenueColumns(),
      ...specialColumns,
    ];
  };

  const handleExpandedKeysChange = (keys, { event }) => {
    event.stopPropagation();
    setExpandedKeys(keys);
  };

  /**
   * 获取表格插件配置
   * 根据不同条件组合所需的表格功能插件，包括：
   * - 滚动功能
   * - 分页功能（当 isNotPageableAuto 为 false 时）
   * - 展开功能（当 isExpandable 为 true 时）
   * @returns {Array} 返回配置好的表格插件数组
   * @private
   */
  const getAddons = () => {
    const addons = [
      scrollable({
        minWidth: scrollMinWidth,
      }),
    ];
    if (!isNotPageableAuto) {
      addons.push(
        pageable({
          onPagingChange: () => onPagingChange(),
          pageSizeOptions: pageSizeOptions[0],
          pageSizeVisible: isSingePage,
          pageIndexVisible: isSingePage,
          jumpVisible: isSingePage,
          endJumpVisible: isSingePage,
        }),
      );
    }
    if (isExpandable) {
      addons.push(
        ...[
          expandable({
            // 已经展开的产品
            expandedKeys,
            // 产品展开为消息行
            expand: (record) => record.msgList || null,
            // 发生展开行为时，回调更新展开键值
            onExpandedKeysChange: handleExpandedKeysChange,
            // 只有产品行允许展开
            shouldRecordExpandable: (record) => record.children?.length,
            render(record) {
              return (
                <InsideTable
                  records={record?.children}
                  expandedTableCol={expandedTableCol}
                  onGameItemClick={onGameItemClick}
                  entityType={entityType}
                  keyMap={keyMap}
                  dealMap={dealMap}
                  tableIconMap={tableIconMap}
                />
              );
            },
          }),
        ],
      );
    }
    return addons;
  };
  return (
    <Abnormal
      isLoading={isLoading}
      isNoData={!isLoading && !tableData?.length}
      nodataMsg={nodataMsg}
      component={
        <>
          <Table
            className={classnames('dbi-insights-table dbi-table-sticky', className, {
              'dbi-table-selectable': firstCol[0]?.hasCheckbox,
              'dbi-table-expandable': isExpandable,
              'sub-table-same': isExpandable,
            })}
            records={tableData}
            // 配置行键值
            recordKey={(record: ITableData) =>
              record?.children?.length
                ? record[keyMap.expandParentKey]
                : record[keyMap.expandChildKey] || record[keyMap.expandParentKey]
            }
            bordered="all"
            columns={getAllColumns()}
            addons={getAddons()}
          />
          {isNotPageableAuto && (
            <Pagination
              pageSizeOptions={pageSizeOptions}
              onPagingChange={(e) => {
                if (onPagingChange instanceof Function) {
                  onPagingChange(e);
                }
              }}
              pageSize={pageSize}
              pageIndex={pageIndex}
              recordCount={total}
              stateTextVisible={true}
              pageSizeVisible={isSingePage}
              pageIndexVisible={isSingePage}
              jumpVisible={isSingePage}
              endJumpVisible={isSingePage}
              stateText={formatMessage({ id: 'dbi_pagination_total' }, { total: total ?? 0 })}
            />
          )}
        </>
      }
    />
  );
}
